import React, { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import { type Group, groupBlock } from './util/groupBlock';
import { PAGE_UI_CONFIG } from './const';
import { QuizWorksheetTwoColumnLayout } from './QuizWorksheetTwoColumnLayout';
import { type BlockData } from './type';

/**
 * 페이지에 표시하기 전, 아이템들의 높이를 측정하기 위한 컴포넌트
 */

export const InvisiblePageChunkGenerator = <T extends BlockData<string, unknown>,>({
  blocks,
  renderer,
  onGenerateComplete
}: {
  blocks: T[];
  renderer: React.FC<{
    props: T;
  }>;
  onGenerateComplete: (pagesWithTwoColumns: {
    leftColumn: ReactNode[];
    rightColumn: ReactNode[];
  }[]) => void;
}) => {
  const [internalNodes, setInternalNodes] = useState<ReactNode[]>([]);
  const itemDomHeightMap = useRef<Record<number, {
    type: string;
    domHeight: number;
  }>>({});
  useEffect(() => {
    const nodes = blocks.map((d, index) => {
      const itemNode = <div className={'w-full'} key={index} ref={dom => {
        if (dom) {
          const domHeight = d.type === 'question' ? dom.offsetHeight + PAGE_UI_CONFIG.questionItemGap : dom.offsetHeight;
          itemDomHeightMap.current[index] = {
            type: d.type,
            domHeight
          };
        }
      }}>
          {renderer({
          props: d
        })}
        </div>;
      return itemNode;
    });
    setInternalNodes(nodes);
  }, [blocks, renderer]);
  useEffect(() => {
    const pagesWithTwoColumns = getPagesWithTwoColumns(internalNodes, itemDomHeightMap.current);
    onGenerateComplete(pagesWithTwoColumns);
  }, [internalNodes, onGenerateComplete]);

  //pdf view로 보여질 layout에 퀴즈 item들을 한 번 rendering 한 후 페이지를 할당한다.
  //페이지 할당 시에는 모든 퀴즈 아이템들을 단순하게 한 줄로 세워놓고, static한 페이지 높이 단위로 아이템들을 나눈다.
  return <QuizWorksheetTwoColumnLayout leftColumnItems={internalNodes} rightColumnItems={[]} isHidden={true} data-sentry-element="QuizWorksheetTwoColumnLayout" data-sentry-component="InvisiblePageChunkGenerator" data-sentry-source-file="InvisiblePageChunkGenerator.tsx" />;
};
const getPagesWithTwoColumns = (nodes: ReactNode[], domItemHeightMap: Record<number, {
  type: string;
  domHeight: number;
}>) => {
  const isItemExceedColumnHeight = (remainingHeight: number, itemHeight: number) => {
    return remainingHeight < itemHeight;
  };
  const renderGroup = (group: Group) => {
    return <Fragment>
        {group.blocks.map((block, index) => <Fragment key={index}>{block.node}</Fragment>)}
      </Fragment>;
  };

  // 아이템들이 들어갈 수 있는 최대 높이
  const itemAreaHeight = PAGE_UI_CONFIG.columnHeight - PAGE_UI_CONFIG.columnPaddingTop;
  const groups = groupBlock(nodes.map((node, i) => ({
    node,
    type: domItemHeightMap[i].type,
    height: domItemHeightMap[i].domHeight
  })), itemAreaHeight);
  const pagesWithTwoColumns: {
    leftColumn: ReactNode[];
    rightColumn: ReactNode[];
  }[] = [];

  // 왼쪽, 오른쪽 컬럼의 남은 높이
  // 노드들을 순회하면서, 노드가 페이지에 그려질 때의 높이를 계산하여
  // 해당 노드에 해당하는 dom이 들어간 column의 남은 높이를 차감한다.
  let remainingLeftColumnHeight = itemAreaHeight;
  let remainingRightColumnHeight = itemAreaHeight;
  let leftColumnNodes: ReactNode[] = [];
  let rightColumnNodes: ReactNode[] = [];
  groups.forEach(group => {
    const {
      totalHeight
    } = group;
    // 왼쪽 컬럼에 노드를 넣을 수 없는 경우
    if (isItemExceedColumnHeight(remainingLeftColumnHeight, totalHeight)) {
      //왼쪽 column의 남은 높이는 0으로 처리한다.
      remainingLeftColumnHeight = 0;
      // 오른쪽 컬럼에도 노드를 넣을 수 없는 경우
      if (isItemExceedColumnHeight(remainingRightColumnHeight, totalHeight)) {
        // 페이지를 생성한다.
        pagesWithTwoColumns.push({
          leftColumn: [...leftColumnNodes],
          rightColumn: [...rightColumnNodes]
        });
        // 다음 페이지의 왼쪽 컬럼에 노드를 넣고, 남은 왼쪽 컬럼 높이를 초기화한다.
        leftColumnNodes = [renderGroup(group)];
        remainingLeftColumnHeight = itemAreaHeight - totalHeight;
        // 다음 페이지의 오른쪽 컬럼 값 역시 초기화한다.
        rightColumnNodes = [];
        remainingRightColumnHeight = itemAreaHeight;
      } else {
        // 오른쪽 컬럼에 노드를 넣을 수 있는 경우, 노드를 넣고
        // 오른쪽 컬럼의 남은 높이를 차감한다.
        remainingRightColumnHeight -= totalHeight;
        rightColumnNodes.push(renderGroup(group));
      }
    } else {
      // 왼쪽 컬럼에 노드를 넣을 수 있는 경우, 노드를 넣고
      // 왼쪽 컬럼의 남은 높이를 차감한다.
      remainingLeftColumnHeight -= totalHeight;
      leftColumnNodes.push(renderGroup(group));
    }
  });

  // 모든 노드를 순회한 후 노드가 들어있는 컬럼이 있는 경우,
  // 페이지를 생성한다.
  if (leftColumnNodes.length > 0 || rightColumnNodes.length > 0) {
    pagesWithTwoColumns.push({
      leftColumn: [...leftColumnNodes],
      rightColumn: [...rightColumnNodes]
    });
  }
  return pagesWithTwoColumns;
};